import React, { useState } from "react";
import { isAndroid } from "react-device-detect";

import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";

import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";

import Button from "components/Button";

import { ReactComponent as CheckIcon } from "assets/images/icon_check.svg";
import { ReactComponent as CopyIcon } from "assets/images/icon_copy-attach.svg";
import { ReactComponent as CloseIcon } from "assets/images/icon_delete_input.svg";
import { ReactComponent as PointOfSaleIcon } from "assets/images/icon_point-of-sale.svg";

import { isInAppBrowser } from "shared/constants/isInAppBrowser";

import analytics from "services/analytics";
import { AnalyticsEvents } from "shared/constants/analytics";

import { styles } from "./styles";

enum CopyStatus {
  InProgress = "in_progress",
  Success = "success",
  Error = "error",
}

interface Props {
  url: string;
  open: boolean;
  onClose: () => void;
  showChromeButton?: boolean;
}

const InfoModal: React.FC<Props> = (props) => {
  const { url, open, showChromeButton, onClose } = props;
  const [copyStatus, setCopyStatus] = useState<CopyStatus | null>(null);

  const inAppBrowser = isInAppBrowser();
  const timestamp = new Date().getTime();

  const onCopyLink = async () => {
    try {
      setCopyStatus(CopyStatus.InProgress);
      await navigator.clipboard.writeText(url + `?t=${timestamp}`);
      analytics().sendEvent(AnalyticsEvents.iban_qr_pay_modal_copy);
      setCopyStatus(CopyStatus.Success);

      setTimeout(() => {
        setCopyStatus(null);
      }, 1000);
    } catch (err) {
      setCopyStatus(CopyStatus.Error);
    }
  };

  const constructChromeUrl = () => {
    const chromeUrl =
      url.replace("https://", "intent://") +
      `?t=${timestamp}#Intent;scheme=https;package=com.android.chrome;end`;

    return chromeUrl;
  };

  const onOpenInChrome = () => {
    analytics().sendEvent(AnalyticsEvents.iban_qr_pay_modal_chrome);
  };

  const onCloseModal = () => {
    analytics().sendEvent(AnalyticsEvents.iban_qr_pay_modal_close);
    onClose();
  };

  const renderButtonStyle = () => {
    switch (copyStatus) {
      case CopyStatus.InProgress:
      case CopyStatus.Success:
        return "primary";
      case CopyStatus.Error:
        return "error";
      default:
        return "secondary";
    }
  };

  const renderButtonContent = () => {
    switch (copyStatus) {
      case CopyStatus.Success:
        return (
          <>
            <CheckIcon style={{ marginRight: 1.5 }} />
            Посилання скопійоване
          </>
        );
      case CopyStatus.Error:
        return "Помилка копіювання";
      default:
        return (
          <>
            <CopyIcon style={{ marginRight: 1.5 }} />
            Копіювати посилання
          </>
        );
    }
  };

  return (
    <Dialog open={open} onClose={onClose} PaperProps={{ xs: styles.modal }}>
      <DialogTitle component="div" sx={styles.title}>
        <IconButton aria-label="close-modal" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={styles.content}>
        <PointOfSaleIcon />
        <Typography variant="h5" mt={3}>
          {inAppBrowser ? "Ой, щось не так!" : "Ой, щось не так!"}
        </Typography>
        <Typography color="grey800" mt={0.5}>
          {inAppBrowser
            ? "Налаштування вашого додатку не дозволяє нам автоматично перенести дані для оплати."
            : "Ваш веб-переглядач незміг автоматично перенести дані для оплати."}
        </Typography>
        <Typography color="grey800" mt={3}>
          {inAppBrowser
            ? "Скопіюйте посилання на оплату та відкрийте його у іншому веб-переглядачі на вашому пристрої (наприклад, Chrome або Safari)."
            : "Переконайтеся, що додаток встановлено, або відкрийте посилання на оплату в іншому веб-переглядачі на вашому пристрої (Chrome або Safari)."}
        </Typography>
        {inAppBrowser && (
          <>
            <Button
              sx={{ mt: 2 }}
              size="medium"
              variant="contained"
              color={renderButtonStyle()}
              fullWidth
              onClick={onCopyLink}
              loading={copyStatus === CopyStatus.InProgress}
            >
              {renderButtonContent()}
            </Button>

            {isAndroid && showChromeButton && (
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={onOpenInChrome}
                sx={{ mt: 2 }}
                href={constructChromeUrl()}
              >
                Відкрити в Chrome
              </Button>
            )}
          </>
        )}
        <Button
          variant="contained"
          color="secondary"
          fullWidth
          onClick={onCloseModal}
          sx={{ mt: 2 }}
        >
          Закрити
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default InfoModal;
